import { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmLeadService } from '../../kanban-board/services/crm-lead.service';
import { LeadResponse } from '../../kanban-board/services/responses/lead.response';
import { LeadBlock } from './lead-block';

interface Props {
    lead: LeadResponse;
    onChanged: () => void;
}

/// A descrição do lead: texto único que se sobrescreve; o registro por momento é a nota do histórico.
export const LeadNote = ({ lead, onChanged }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(lead.description ?? '');
    const [saving, setSaving] = useState(false);

    // O lead recarrega após qualquer ação: sem isto o campo ficaria preso ao valor inicial.
    useEffect(() => {
        if (!editing) setText(lead.description ?? '');
    }, [lead.description, editing]);

    const save = async () => {
        setSaving(true);

        try {
            // O endpoint atualiza o lead inteiro, então os outros campos vão como estão.
            await new CrmLeadService().update(lead.id, {
                statusId: lead.statusId,
                contactName: lead.contactName,
                contactPhone: lead.contactPhone,
                description: text.trim() || null,
                value: lead.value ?? null,
                procedureName: lead.procedureName ?? null,
            });

            setEditing(false);
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        setText(lead.description ?? '');
        setEditing(false);
    };

    const buttonSx = (color: string) => ({
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.6,
        py: 0.6,
        borderRadius: '8px',
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
        color: cc.onTint,
        border: `1px solid ${color}66`,
        bgcolor: `${color}1f`,
    });

    if (!editing) {
        return (
            <LeadBlock
                columns={3}
                title={t('telaLead.descricao')}
                action={{
                    label: t('general.editar'),
                    icon: <EditOutlinedIcon sx={{ fontSize: 15 }} />,
                    onClick: () => setEditing(true),
                }}
            >
                <Typography
                    sx={{
                        fontSize: 12.5,
                        color: lead.description ? cc.textPrimary : cc.textSecondary,
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {lead.description || t('telaLead.semDescricao')}
                </Typography>
            </LeadBlock>
        );
    }

    return (
        <LeadBlock columns={3} title={t('telaLead.descricao')}>
            {/* Ocupa o bloco inteiro: caixa encolhida num retângulo largo parecia remendo. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <TextField
                    multiline
                    fullWidth
                    autoFocus
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('telaLead.descricaoPlaceholder')}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            height: '100%',
                            alignItems: 'flex-start',
                            bgcolor: cc.surfaceBg,
                            borderRadius: '9px',
                            color: cc.textPrimary,
                            '& fieldset': { borderColor: cc.border },
                        },
                        '& .MuiInputBase-input': { color: cc.textPrimary, fontSize: 12.5 },
                    }}
                />

                <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={saving ? undefined : save}
                        onKeyDown={(e) => e.key === 'Enter' && !saving && save()}
                        sx={{ ...buttonSx(cc.eventSuccess), opacity: saving ? 0.5 : 1 }}
                    >
                        {t('general.salvar')}
                    </Box>
                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={cancel}
                        onKeyDown={(e) => e.key === 'Enter' && cancel()}
                        sx={buttonSx(cc.textSecondary)}
                    >
                        {t('general.cancelar')}
                    </Box>
                </Box>
            </Box>
        </LeadBlock>
    );
};
